import { forwardRef } from "react";
import { Phone, Mail, MapPin } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDomainConfig } from "@/contexts/DomainConfigContext";
import { useLanguage } from "@/contexts/LanguageContext";

const Footer = forwardRef<HTMLElement>((_, ref) => {
  const { config } = useDomainConfig();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const phoneNumber = config?.customer_service || "";
  const email = config?.email || "";
  const address = config?.address || "";
  const logoUrl = config?.logo ? `https://globalparkingtech.co.uk/logos/${config.logo}` : "";
  const title = config?.title || "Southampton Port Parking";
  const legalName = config?.legal_name || config?.title || "Southampton Port Parking";

  const handleScrollToSection = (sectionId: string) => {
    if (location.pathname === "/") {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(`/#${sectionId}`);
    }
  };

  const navLinks = [
    { name: t("nav.home"), href: "/", isSection: false },
    { name: t("nav.about"), href: "/about", isSection: false },
    { name: "Services", href: "/services", isSection: false },
    { name: t("nav.faqs"), href: "/faqs", isSection: false },
    { name: "Blog", href: "/blogs", isSection: false },
    { name: t("nav.contact"), href: "/contact", isSection: false },
  ];

  const legalLinks = [
    { name: "Terms & Conditions", href: "/terms", isSection: false },
    { name: "Privacy Policy", href: "/privacy", isSection: false },
  ];

  return (
    <footer ref={ref} id="contact" className="bg-primary border-t border-primary pt-16 pb-8">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-12">
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="mb-4 inline-block">
              {logoUrl && (
                <img
                  src={logoUrl}
                  alt={title}
                  className="h-10 sm:h-12 w-auto object-contain bg-white rounded-xl p-1.5"
                  width={200}
                  height={40}
                />
              )}
            </Link>
            <p className="text-primary-foreground/70 text-sm leading-relaxed mt-3 max-w-xs text-justify">
              Southampton Port Parking offers secure Park and Stroll and Park and Ride services for cruise and ferry travellers — affordable, reliable, and stress-free.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-4 text-primary-foreground">Quick Links</h4>
            <ul className="space-y-2">
              {navLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-primary-foreground/70 hover:text-primary-foreground transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-bold text-lg mb-4 text-primary-foreground">Legal</h4>
            <ul className="space-y-2">
              {legalLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-primary-foreground/70 hover:text-primary-foreground transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-bold text-lg mb-4 text-primary-foreground">{t("footer.contact")}</h4>
            <div className="space-y-3">
              {phoneNumber && (
                <a
                  href={`tel:${phoneNumber.replace(/\s/g, "")}`}
                  className="flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground transition-colors text-sm"
                >
                  <Phone className="w-4 h-4 text-primary-foreground" />
                  <span>{phoneNumber}</span>
                </a>
              )}
              {email && (
                <a
                  href={`mailto:${email}`}
                  className="flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground transition-colors text-xs sm:text-sm"
                >
                  <Mail className="w-4 h-4 text-primary-foreground flex-shrink-0" />
                  <span className="break-all">{email}</span>
                </a>
              )}
              {address && (
                <div className="flex items-center gap-2 text-primary-foreground/70 text-xs sm:text-sm mt-2">
                  <MapPin className="w-4 h-4 text-primary-foreground flex-shrink-0" />
                  <span>{address}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-primary-foreground/20 pt-8">
          <p className="text-center text-primary-foreground/70 text-sm">
            © 2026 {legalName}. {t("footer.rights")}
          </p>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = "Footer";

export default Footer;
